/**
 * HomeTopCards — SLIM status strip (redesign 2026-08-06)
 * -------------------------------------------------------------------------
 * The user rejected the previous "two big square gauge cards" layout
 * ("the task and daily passcode tiles are huge"). This replacement is
 * a single-row status strip that fits in ~64px of vertical space and
 * still surfaces both pieces of state at a glance:
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │  ✓  Today's Tasks       0 / 3      →                            │
 *   │  ⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯⎯   │
 *   │  ⎘  Daily Passcode      Ready      →                            │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Each row is a big <button> that opens the same sheet as before.
 * State is conveyed by a small pill on the right:
 *   • Tasks — "0 / 3" tabular numerals with a color that reflects done
 *   • Passcode — "Ready" / "Snoozing · tap" / "Scan today" caption
 *
 * Compared to the previous design:
 *   • ~64 px tall vs ~180 px tall → 3× more content above the fold
 *   • Reads left-to-right as a status line, not a "dashboard" (which
 *     was overkill for two numbers)
 *   • Single card container (fewer visible edges, less noise)
 *   • Same testids so the wiring in HomePage.js doesn't need touching
 */
import React from "react";
import { CheckSquare, Barcode as BarcodeIcon, ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function HomeTopCards({
  taskSummary = { done: 0, total: 0 },
  passcodeReady = false,
  onOpenTasks,
  onOpenPasscode,
}) {
  const { done, total, inProgress = 0 } = taskSummary;

  // Pill state for Tasks.
  let taskPill = { label: "no tasks", cls: "bg-slate-100 text-slate-500" };
  if (total > 0) {
    if (done === total) {
      taskPill = { label: "all done", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" };
    } else if (inProgress > 0) {
      taskPill = { label: `${done} / ${total}`, cls: "bg-cyan-50 text-cyan-700 border border-cyan-200" };
    } else if (done > 0) {
      taskPill = { label: `${done} / ${total}`, cls: "bg-slate-50 text-slate-700 border border-slate-200" };
    } else {
      taskPill = { label: `0 / ${total}`, cls: "bg-amber-50 text-amber-800 border border-amber-200" };
    }
  }

  const passcodePill = passcodeReady
    ? { label: "Ready", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" }
    : { label: "Tap to scan", cls: "bg-blue-50 text-blue-700 border border-blue-200" };

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden"
      style={{
        border: "1px solid rgba(148,163,184,0.14)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 2px 8px -2px rgba(15,23,42,0.05)",
      }}
      data-testid="home-status-strip"
    >
      {/* ─── TASKS ROW ─── */}
      <button
        type="button"
        onClick={onOpenTasks}
        className="group w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 active:bg-slate-100/60 transition-colors text-left"
        aria-label={`Tasks: ${done} of ${total} completed. Tap to view.`}
        data-testid="home-tasks-card"
      >
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-slate-100 flex-shrink-0">
          {total > 0 && done === total ? (
            <CheckCircle weight="fill" className="w-5 h-5 text-emerald-500" />
          ) : (
            <CheckSquare weight="duotone" className="w-5 h-5 text-slate-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-black text-slate-900 tracking-tight leading-tight">Today&rsquo;s Tasks</p>
          <p className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate">
            {total === 0
              ? "You\u2019re all caught up"
              : done === total
                ? "All wins \u2014 nice work"
                : inProgress > 0
                  ? `${inProgress} in progress`
                  : "Tap to see what\u2019s left"}
          </p>
        </div>
        <span className={`text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full tabular-nums flex-shrink-0 ${taskPill.cls}`} data-testid="home-tasks-pill">
          {taskPill.label}
        </span>
        <ArrowRight weight="bold" className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      </button>

      {/* thin hairline divider */}
      <div className="h-px bg-slate-100 mx-4" aria-hidden />

      {/* ─── PASSCODE ROW ─── */}
      <button
        type="button"
        onClick={onOpenPasscode}
        className="group w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 active:bg-slate-100/60 transition-colors text-left"
        aria-label={
          passcodeReady
            ? "Daily Passcode ready. Tap to show barcode."
            : "Daily Passcode not yet scanned. Tap to scan."
        }
        data-testid="home-passcode-card"
      >
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-slate-100 flex-shrink-0">
          <BarcodeIcon weight="duotone" className="w-5 h-5 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-black text-slate-900 tracking-tight leading-tight">Daily Passcode</p>
          <p className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate">
            {passcodeReady ? "Ready to show at the register" : "Snoozing \u2014 scan today\u2019s to unlock"}
          </p>
        </div>
        <span className={`text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0 ${passcodePill.cls}`} data-testid="home-passcode-pill">
          {passcodePill.label}
        </span>
        <ArrowRight weight="bold" className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      </button>
    </div>
  );
}
